import { and, eq } from "drizzle-orm";
import { db } from "../../db/index";
import { moduleSettings } from "../../db/schema/rbac";
import { getEffectivePermissions, getRoleEffectivePermissions } from "../../rbac/index";
import { SCOPE } from "../../rbac/scopes";

/**
 * Server-side navigation tree. Each item declares the view-permission and/or module that gates
 * it; `buildNavigation` returns only the items the user may see — legacy hides sidebar items the
 * role can't access and modules the company hasn't enabled (reskin-docs/02 §1,3,5). Self-service
 * items have no perm (own data, always shown). Tickets removed (operator). Icon names map to
 * lucide-react on the client.
 */
export type NavItem = { label: string; path: string; icon: string; perm?: string; module?: string; allOnly?: boolean };
export type NavGroup = { heading?: string; items: NavItem[] };

const NAV: NavGroup[] = [
  { items: [{ label: "Dashboard", path: "/dashboard", icon: "LayoutDashboard" }] },
  {
    heading: "Workspace",
    items: [
      { label: "Projects", path: "/projects", icon: "FolderKanban", perm: "view_projects" },
      { label: "Tasks", path: "/tasks", icon: "ListChecks", perm: "view_tasks" },
      { label: "Time logs", path: "/timelogs", icon: "Timer", perm: "view_timelogs" },
      { label: "Events", path: "/events", icon: "CalendarDays", perm: "view_events" },
    ],
  },
  {
    heading: "HR & People",
    items: [
      { label: "Employees", path: "/employees", icon: "Users", perm: "view_employees" },
      { label: "Departments", path: "/departments", icon: "Network", perm: "view_department" },
      { label: "Designations", path: "/designations", icon: "GitBranch", perm: "view_designation" },
      { label: "Attendance", path: "/attendance", icon: "CalendarClock", perm: "view_attendance", allOnly: true },
      { label: "Work From Home", path: "/wfh", icon: "Home" },
      { label: "Corrections", path: "/corrections", icon: "Wrench" },
      { label: "Leaves", path: "/leaves", icon: "CalendarRange", perm: "view_leave" },
      { label: "Appreciations", path: "/appreciations", icon: "Award", perm: "view_appreciation" },
      { label: "Holidays", path: "/holidays", icon: "Palmtree", perm: "view_holiday" },
      { label: "Shifts", path: "/shifts", icon: "Clock", perm: "view_shift_roster" },
      { label: "Promotions", path: "/promotions", icon: "TrendingUp", perm: "view_attendance", allOnly: true },
      { label: "Compliance", path: "/compliance", icon: "ShieldCheck", perm: "view_attendance", allOnly: true },
      { label: "Letters", path: "/letters", icon: "FileText", perm: "view_letter", module: "letter" },
      { label: "Lifecycle", path: "/lifecycle", icon: "Workflow", perm: "view_lifecycle_event", module: "lifecycle" },
    ],
  },
  {
    heading: "Finance",
    items: [
      { label: "Proposals", path: "/proposals", icon: "FileText", perm: "view_lead_proposals" },
      { label: "Estimates", path: "/estimates", icon: "FileText", perm: "view_estimates" },
      { label: "Invoices", path: "/invoices", icon: "ReceiptText", perm: "view_invoices" },
      { label: "Payments", path: "/payments", icon: "Wallet", perm: "view_payments" },
      { label: "Expenses", path: "/expenses", icon: "CreditCard", perm: "view_expenses" },
      { label: "Credit notes", path: "/credit-notes", icon: "FileMinus", perm: "view_credit_notes" },
      { label: "Bank accounts", path: "/bank-accounts", icon: "Landmark", perm: "view_bankaccount" },
      { label: "Contracts", path: "/contracts", icon: "FileSignature", perm: "view_contract" },
      { label: "Products", path: "/products", icon: "Package", perm: "view_product" },
      { label: "Orders", path: "/orders", icon: "ShoppingCart", perm: "view_order", module: "orders" },
      { label: "Payroll", path: "/payroll", icon: "Banknote", perm: "view_payroll", module: "payroll" },
    ],
  },
  {
    heading: "CRM",
    items: [
      { label: "Leads", path: "/leads", icon: "Contact", perm: "view_lead" },
      { label: "Deals", path: "/deals", icon: "Handshake", perm: "view_deals" },
      { label: "Clients", path: "/clients", icon: "Building2", perm: "view_clients" },
      { label: "Knowledge base", path: "/knowledge-base", icon: "BookOpen", perm: "view_knowledgebase" },
    ],
  },
  {
    heading: "Modules",
    items: [
      { label: "Recruit", path: "/recruit", icon: "UserSearch", perm: "view_job", module: "recruit" },
      { label: "Performance", path: "/performance", icon: "Target", module: "performance" },
      { label: "Assets", path: "/assets", icon: "Box", perm: "view_asset", module: "asset" },
    ],
  },
  {
    heading: "Me",
    items: [
      { label: "My Attendance", path: "/me/attendance", icon: "CalendarClock" },
      { label: "My Leaves", path: "/me/leaves", icon: "CalendarRange" },
      { label: "My Payslips", path: "/me/payslips", icon: "Wallet" },
      { label: "My Documents", path: "/me/documents", icon: "Folder" },
      { label: "My Calendar", path: "/me/calendar", icon: "Calendar" },
      { label: "Reimbursements", path: "/me/reimbursements", icon: "Receipt" },
      { label: "My Profile", path: "/me/profile", icon: "UserCircle" },
    ],
  },
  {
    items: [{ label: "Settings", path: "/settings", icon: "Settings", perm: "manage_role_permission_setting" }],
  },
];

/**
 * The filtered nav tree: item shown when its perm is granted (scope≠NONE) and its module is enabled.
 * `asRole` (admin-only, enforced by the caller) previews the nav as that role would see it.
 */
export async function buildNavigation(
  userId: number,
  companyId: number,
  asRole?: string,
): Promise<NavGroup[]> {
  const eff = asRole
    ? await getRoleEffectivePermissions(companyId, asRole)
    : await getEffectivePermissions(userId);
  const activeModules = new Set(
    (
      await db
        .select({ m: moduleSettings.moduleName })
        .from(moduleSettings)
        .where(and(eq(moduleSettings.companyId, companyId), eq(moduleSettings.status, "active")))
    ).map((r) => r.m),
  );

  const can = (it: NavItem) => {
    if (it.module && !activeModules.has(it.module)) return false;
    if (it.perm) {
      const s = eff[it.perm] ?? SCOPE.NONE;
      if (s === SCOPE.NONE) return false;
      if (it.allOnly && s !== SCOPE.ALL) return false; // admin/HR-only items (e.g. Promotions)
    }
    return true;
  };

  return NAV.map((g) => ({ heading: g.heading, items: g.items.filter(can) })).filter(
    (g) => g.items.length > 0,
  );
}
